import React, { useEffect, useState } from 'react';
import { ArrowRight, Eye, EyeOff, Loader2, Fingerprint } from 'lucide-react';
import { API } from '../lib/api';
import axios from 'axios';
import { toast } from 'sonner';
import InstallAppPanel from '../components/InstallAppPanel';
import AppBuildBadge from '../components/AppBuildBadge';
import { setItem as persistItem, disableBiometric } from '../lib/storage';
import {
  checkBiometric, promptBiometric,
  setBiometricCredentials, getBiometricCredentials, deleteBiometricCredentials,
} from '../lib/biometric';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mustChangePw, setMustChangePw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [activeToken, setActiveToken] = useState('');

  // Biometric state — only relevant on Capacitor mobile builds.
  const [bio, setBio] = useState({ available: false, type: 'Fingerprint' });
  const [bioPromptOpen, setBioPromptOpen] = useState(false);
  const [pendingPostLogin, setPendingPostLogin] = useState(null); // { name }
  // Live stats for the hero panel — module count + active-site count. Falls
  // back to sensible defaults if the public endpoint is unreachable.
  const [stats, setStats] = useState({ modules: 29, active_projects: null });

  useEffect(() => {
    // On mount, see if the device has a usable biometric. Drives both the
    // "Unlock with Fingerprint" button (when a saved token exists) and the
    // opt-in dialog after first password login.
    checkBiometric().then(setBio);
    axios.get(`${API}/public/landing-stats`)
      .then(r => setStats(s => ({ ...s, ...r.data })))
      .catch(() => {});
  }, []);

  // If the user previously enabled biometric, surface the unlock button — even
  // after an explicit logout, because the credentials live in the OS keystore
  // (not tied to the in-app session token). Tapping it does a fresh login.
  const biometricEnabled = typeof window !== 'undefined' ? localStorage.getItem('biometric_enabled') === '1' : false;
  const lastEmail = typeof window !== 'undefined' ? localStorage.getItem('last_login_email') : null;
  const canUnlock = bio.available && biometricEnabled;

  // On mobile (<768px) go straight to /m to avoid the brief desktop
  // Layout render that can crash on mobile browsers before MobileAutoRedirect fires.
  const getPostLoginUrl = () =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? '/m' : '/app';

  const unlockWithBiometric = async () => {
    setLoading(true);
    try {
      await promptBiometric(`Unlock as ${lastEmail || 'your account'}`);
      const creds = await getBiometricCredentials();
      if (!creds) {
        toast.error('No saved login found. Please sign in with your password once.');
        await disableBiometric();
        return;
      }
      const { data } = await axios.post(`${API}/auth/login`, {
        email: creds.username, password: creds.password,
      });
      if (data.access_token) {
        await persistItem('access_token', data.access_token);
        await persistItem('last_login_email', creds.username);
      }
      if (data.must_change_password) {
        setActiveToken(data.access_token);
        setPassword(creds.password);
        setMustChangePw(true);
        toast.info('You must change your password before continuing.');
        return;
      }
      window.location.href = getPostLoginUrl();
    } catch (err) {
      // 401 ⇒ stored password is stale (changed elsewhere). Drop biometric so
      // the user re-enrolls after a fresh password login.
      if (err?.response?.status === 401) {
        await deleteBiometricCredentials();
        await disableBiometric();
        toast.error('Saved login expired. Please sign in with your password.');
      } else if (err?.response) {
        toast.error(formatApiError(err?.response?.data?.detail));
      } else {
        toast.error('Biometric cancelled');
      }
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = (user) => {
    toast.success(`Welcome back, ${user.name || user.email}`);
    window.location.href = getPostLoginUrl();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    const prevEmail = typeof window !== 'undefined' ? localStorage.getItem('last_login_email') : null;
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: email.trim(), password },
      );
      // Store token for Bearer auth (cookies are blocked on Safari due to
      // platform proxy returning `Allow-Origin: *` with credentials).
      // persistItem writes to BOTH localStorage AND native Preferences on
      // mobile so the JWT survives app updates.
      if (data.access_token) {
        await persistItem('access_token', data.access_token);
        await persistItem('last_login_email', email.trim());
        setActiveToken(data.access_token);
      }
      // If biometric was enrolled for a DIFFERENT user on this device, reset it
      // so the new user can enroll their own credentials.
      let enrolled = localStorage.getItem('biometric_enabled') === '1';
      if (enrolled && prevEmail && prevEmail !== email.trim()) {
        await disableBiometric();
        enrolled = false;
      }
      if (data.must_change_password) {
        setMustChangePw(true);
        toast.info('You must change your password before continuing.');
      } else if (bio.available && !enrolled) {
        // Mobile + biometric hardware present + user hasn't opted in yet.
        // Pop the opt-in card; finishLogin() will be called from there.
        setPendingPostLogin({ name: data.user.name || data.user.email });
        setBioPromptOpen(true);
      } else {
        finishLogin(data.user);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error('Passwords do not match');
      return;
    }
    // Validation must match backend _is_password_acceptable() policy exactly
    const SPECIALS = /[!@#$%^&*()_+\-=[\]{};':",./<>?\\|`~]/;
    if (newPw.length < 12) {
      toast.error('New password must be at least 12 characters long.');
      return;
    }
    if (!/[a-z]/.test(newPw)) {
      toast.error('New password must contain at least one lowercase letter.');
      return;
    }
    if (!/[A-Z]/.test(newPw)) {
      toast.error('New password must contain at least one uppercase letter.');
      return;
    }
    if (!/\d/.test(newPw)) {
      toast.error('New password must contain at least one number.');
      return;
    }
    if (!SPECIALS.test(newPw)) {
      toast.error('New password must contain at least one special character (e.g. !@#$%).');
      return;
    }
    setLoading(true);
    try {
      const { data: cpData } = await axios.post(
        `${API}/auth/change-password`,
        { current_password: password, new_password: newPw },
        { headers: activeToken ? { Authorization: `Bearer ${activeToken}` } : {} }
      );
      // Persist the freshly-issued token so the next /auth/me works.
      if (cpData?.access_token) {
        await persistItem('access_token', cpData.access_token);
      }
      toast.success('Password updated. Logging you in…');
      window.location.href = getPostLoginUrl();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden lg:block">
        <img
          src="/assets/login-hero.jpg"
          alt="Aerial view of a completed industrial complex"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/60 to-[#0F172A]/70" />
        <div className="absolute inset-0 blueprint-bg opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center">
            <img
              src="/assets/ellora-group-white.png"
              alt="Ellora Group"
              className="h-14 lg:h-16 w-auto object-contain"
              data-testid="login-hero-logo"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 mb-3 font-display font-bold">Engineering · Procurement · Construction</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold leading-[1.05] mb-6">
              Build at the speed<br />of decisions.
            </h1>
            <p className="text-white/80 max-w-md text-sm leading-relaxed">
              A unified command center for projects, procurement, finance, HR, quality and safety — engineered for India's leading EPC firms.
            </p>
          </div>
          <div className="space-y-5">
            {/* FY mission — the company goal, framed as a target to drive toward */}
            <div className="border-l-2 border-blue-400 pl-4" data-testid="login-fy-mission">
              <div className="text-[10px] uppercase tracking-[0.22em] text-blue-300 mb-1.5 font-display font-bold">FY 2026-27 Mission</div>
              <div className="font-display font-extrabold text-4xl lg:text-[2.75rem] leading-none text-white">
                ₹1,000 Cr<span className="text-blue-400">.</span>
              </div>
              <div className="text-white/70 text-sm mt-1.5 max-w-xs">One order book in sight — engineered milestone by milestone.</div>
            </div>
            {/* Live stats — real-time module + active-site counts */}
            <div className="flex gap-8 text-xs uppercase tracking-widest text-white/55 font-display">
              <div data-testid="login-stat-modules">
                <span className="text-white font-bold text-2xl block font-display">{stats.modules ?? '—'}</span>Modules
              </div>
              <div data-testid="login-stat-sites">
                <span className="text-white font-bold text-2xl block font-display">{stats.active_projects ?? '—'}</span>Active Sites
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 relative blueprint-bg">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center mb-10">
            <div className="bg-[#0F172A] rounded-sm px-4 py-3">
              <img src="/assets/ellora-group-white.png" alt="Ellora Group" className="h-10 w-auto object-contain" data-testid="login-mobile-logo" />
            </div>
          </div>

          {!mustChangePw ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#3b4872] mb-3 font-display font-bold">Restricted Access</div>
              <h2 className="font-display text-4xl font-extrabold mb-3 tracking-tight">Sign in to continue.</h2>
              <p className="text-slate-600 mb-8 text-sm leading-relaxed">
                Use the credentials provided by HR. If you've forgotten your password, ask HR/Admin to reset it.
              </p>

              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                {canUnlock && (
                  <button
                    type="button"
                    onClick={unlockWithBiometric}
                    className="w-full flex items-center justify-center gap-3 bg-[#3b4872] hover:bg-[#2c3658] transition-colors text-white px-6 py-4 rounded-sm mb-2"
                    data-testid="login-biometric-unlock-btn"
                  >
                    <Fingerprint size={20} />
                    <span className="font-display font-medium">Unlock with {bio.type}</span>
                  </button>
                )}
                {canUnlock && lastEmail && (
                  <p className="text-center text-[11px] text-slate-400 -mt-1">
                    Saved as {lastEmail} ·{' '}
                    <button
                      type="button"
                      onClick={async () => { await disableBiometric(); window.location.reload(); }}
                      className="underline hover:text-slate-600"
                      data-testid="login-forget-device-btn"
                    >
                      forget this device
                    </button>
                  </p>
                )}
                {canUnlock && (
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                    <div className="relative text-center"><span className="bg-white px-3 text-[10px] uppercase tracking-widest text-slate-400 font-display">or sign in with password</span></div>
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-600 font-display font-bold">Work Email</label>
                  <input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@elloraepc.com"
                    className="w-full mt-1 px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-[#3b4872] text-sm"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-600 font-display font-bold">Password</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full mt-1 px-4 py-3 pr-10 border border-slate-300 rounded-sm focus:outline-none focus:border-[#3b4872] text-sm"
                      required
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 hover:text-slate-700" tabIndex={-1}>
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-between bg-[#3b4872] hover:bg-[#2d3856] transition-colors duration-200 text-white px-6 py-4 rounded-sm disabled:opacity-60"
                  data-testid="login-submit-btn"
                >
                  <span className="font-display font-medium">{loading ? 'Signing in…' : 'Sign In'}</span>
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 mb-3 font-display font-bold">First Login</div>
              <h2 className="font-display text-3xl font-extrabold mb-3 tracking-tight">Set a new password.</h2>
              <p className="text-slate-600 mb-8 text-sm leading-relaxed">
                For security, please change the default/temporary password before continuing.
              </p>
              <form onSubmit={changePw} className="space-y-4" data-testid="change-pw-form">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-600 font-display font-bold">New Password</label>
                  <input
                    type="password"
                    autoFocus
                    value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    placeholder="Min 12 chars · upper + lower + digit + special"
                    className="w-full mt-1 px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-[#3b4872] text-sm"
                    required minLength={12}
                    data-testid="new-password-input"
                  />
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    Must be ≥12 characters · uppercase &amp; lowercase · number · special character (e.g. !@#$%)
                  </p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-600 font-display font-bold">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)}
                    className="w-full mt-1 px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-[#3b4872] text-sm"
                    required minLength={12}
                    data-testid="confirm-password-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-between bg-[#3b4872] hover:bg-[#2d3856] transition-colors duration-200 text-white px-6 py-4 rounded-sm disabled:opacity-60"
                  data-testid="change-pw-submit-btn"
                >
                  <span className="font-display font-medium">{loading ? 'Updating…' : 'Change Password & Sign In'}</span>
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                </button>
              </form>
            </>
          )}

          <p className="mt-10 text-[11px] text-slate-500 leading-relaxed">
            By continuing you accept the Ellora EPC Pvt Ltd internal access policy. All activity is logged for compliance and audit.
          </p>

          <AppBuildBadge />

          <div className="mt-6">
            <InstallAppPanel />
          </div>
        </div>
      </div>

      {/* Biometric opt-in dialog — shown once on first password login on a
          device that has fingerprint/face hardware. */}
      {bioPromptOpen && pendingPostLogin && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-md w-full max-w-sm p-6 space-y-4" data-testid="biometric-opt-in-modal">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-md bg-[#3b4872]/10 text-[#3b4872]">
                <Fingerprint size={28} />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg leading-tight">Unlock with {bio.type}?</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Skip the password next time</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Welcome, <strong>{pendingPostLogin.name}</strong>. You can unlock Ellora ERP with your
              {' '}{bio.type.toLowerCase()} on this device — even after you sign out. Your credentials
              are saved in your phone's hardware-encrypted keystore and unlocked only by your
              {' '}{bio.type.toLowerCase()}.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={async () => {
                  try {
                    await promptBiometric(`Enable ${bio.type} unlock`);
                    await setBiometricCredentials(email.trim(), password);
                    await persistItem('biometric_enabled', '1');
                    await persistItem('last_login_email', email.trim());
                    toast.success(`${bio.type} unlock enabled`);
                  } catch {
                    toast.error('Biometric not confirmed — you can enable later from Settings.');
                  } finally {
                    setBioPromptOpen(false);
                    finishLogin(pendingPostLogin);
                  }
                }}
                className="w-full bg-[#3b4872] hover:bg-[#2d3856] text-white font-display font-medium px-4 py-3 rounded-sm flex items-center justify-center gap-2"
                data-testid="biometric-enable-btn"
              >
                <Fingerprint size={16} /> Enable {bio.type}
              </button>
              <button
                onClick={() => { setBioPromptOpen(false); finishLogin(pendingPostLogin); }}
                className="w-full text-slate-600 hover:text-slate-800 text-sm font-display"
                data-testid="biometric-skip-btn"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
